import { Badge } from '../../components/atoms/Badge'
import { TableShell } from '../../components/molecules/TableShell'
import { Th, Td } from '../../components/molecules/Table'
import type { StockMovementDto } from '../../../models/stock.model'

/**
 * Shared movement-ledger table for the Warehouse history screens. The
 * product view shows a Variant column; the variant view hides it.
 */
const TYPE_LABEL: Record<string, string> = {
  stocked: 'Stocked',
  returned: 'Returned',
  sold: 'Sold'
}

export function StockLedgerTable({
  items,
  showVariant
}: {
  readonly items: ReadonlyArray<StockMovementDto>
  readonly showVariant: boolean
}): JSX.Element {
  return (
    <TableShell>
      <table className="w-full font-body text-body-sm">
        <thead className="bg-parchment/50 text-text-secondary">
          <tr>
            <Th>Date</Th>
            {showVariant ? <Th>Variant</Th> : null}
            <Th>Movement</Th>
            <Th className="text-right">Qty</Th>
            <Th>Note</Th>
          </tr>
        </thead>
        <tbody>
          {items.map((m) => {
            const isIn = m.direction === 'in'
            return (
              <tr key={m.id} className="border-t border-border">
                <Td className="whitespace-nowrap text-text-secondary">{formatDate(m.occurred_at)}</Td>
                {showVariant ? (
                  <Td>
                    {m.axes_label === null || m.axes_label === '' ? (
                      <span className="text-text-secondary">default</span>
                    ) : (
                      m.axes_label
                    )}
                  </Td>
                ) : null}
                <Td>
                  <Badge tone={isIn ? 'success' : 'error'}>{isIn ? 'In' : 'Out'}</Badge>
                  <span className="ml-2 text-text-secondary">{TYPE_LABEL[m.movement_type] ?? m.movement_type}</span>
                </Td>
                <Td className={`text-right whitespace-nowrap font-mono ${isIn ? 'text-forest' : 'text-terra'}`}>
                  {isIn ? '+' : '−'}{m.quantity_units}
                </Td>
                <Td className="text-text-secondary">{m.note ?? '—'}</Td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </TableShell>
  )
}

function formatDate(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  const y = d.getFullYear()
  const mo = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${mo}-${day}`
}
